import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Checkbox, ListItemText, ListSubheader, Menu, MenuItem, Tooltip } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import UnfoldLessRoundedIcon from "@mui/icons-material/UnfoldLessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import BreadcrumbsComponent from "../../../../components/BreadcrumbsComponent";
import BackgroundLetterAvatarsLogo from "../../../../components/AvatarLogo";
import TaskWorkFlowModalPage from "../../../../components/Modal/TaskWorkFlow";
import WorkHierarchyTable from "../../../../components/WorkHierarchy/WorkHierarchyTable";
import ParentPickerDialog from "../../../../components/WorkHierarchy/ParentPickerDialog";
import {
  assigneeOf,
  countByLevel,
  filterTree,
  flattenAll,
  idsWithChildren,
  isDoneStatus,
  levelOf,
  visibleRows,
} from "../../../../components/WorkHierarchy/treeUtils";
import { updateIssue } from "../../../../common/Redux/Reducer";
import API_BASE_URL from "../../../../components/config/appConfig";
import { useToast } from "../../../../components/Toast";
import "../../../../components/WorkHierarchy/workHierarchy.scss";

const TYPE_FILTERS = ["Epic", "Story", "Task", "Bug"];
const QUICK_FILTERS = [
  { key: "mine", label: "Only my issues" },
  { key: "unassigned", label: "Unassigned" },
  { key: "hideDone", label: "Hide done" },
  { key: "activeSprint", label: "In the active sprint" },
];
const MAX_AVATARS = 6;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("accessToken")}` });
const errorText = (error, fallback) => error?.response?.data?.message || fallback;

const readExpanded = (key) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || "[]"));
  } catch {
    return new Set();
  }
};

/* nodes rebuilt with one issue changed, so a status edit shows at once */
const patchNode = (nodes, id, patch) =>
  nodes.map((node) =>
    String(node._id) === id
      ? { ...node, ...patch }
      : { ...node, children: patchNode(node.children || [], id, patch) }
  );

const WorkItemsPage = () => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { projectSlug } = useParams();
  const { Projects } = useSelector((state) => state.ProjamAppSlice);
  const project = Projects?.selectedProject?.selectedProjectData;
  const projectId = project?.slugString === projectSlug ? project?._id : null;
  const storeContributors = Projects?.selectedProject?.contributors;
  const contributors = useMemo(() => storeContributors || [], [storeContributors]);

  const [tree, setTree] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busyIds, setBusyIds] = useState(new Set());

  const [search, setSearch] = useState("");
  const [people, setPeople] = useState([]);
  const [types, setTypes] = useState([]);
  const [quick, setQuick] = useState([]);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [quickAnchor, setQuickAnchor] = useState(null);
  const [peopleAnchor, setPeopleAnchor] = useState(null);

  const storageKey = `pj-work-expanded:${projectId}`;
  const [expanded, setExpanded] = useState(new Set());
  const [addingUnder, setAddingUnder] = useState(null);
  const [creating, setCreating] = useState(false);
  const [parentTarget, setParentTarget] = useState(null);
  const [openIssue, setOpenIssue] = useState(null);

  const myId = useMemo(() => {
    try {
      return jwtDecode(localStorage.getItem("accessToken") || "")?.id || "";
    } catch {
      return "";
    }
  }, []);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/issues/hierarchy/${projectId}`, { headers: authHeaders() });
      setTree(data?.tree || []);
      setStatuses(data?.statuses || []);
      setLoadError("");
    } catch (error) {
      /* a 404 here means the API predates this page, not that the project
         is empty — say so rather than a generic failure */
      setLoadError(
        error?.response?.status === 404 && !error?.response?.data?.message
          ? "Work items aren't available on this server yet — it needs the latest API update. Please try again after it's deployed."
          : errorText(error, "Could not load work items.")
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setExpanded(readExpanded(storageKey));
    load();
  }, [projectId, load, storageKey]);

  const saveExpanded = (next) => {
    setExpanded(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify([...next]));
    } catch {
      // a private window: the view still works, it just won't be remembered
    }
  };

  const toggle = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    saveExpanded(next);
  };

  /* ------------------------------------------------------------- filtering */
  const filtering = Boolean(search.trim() || people.length || types.length || quick.length);

  const filteredTree = useMemo(() => {
    if (!filtering) return tree;
    const term = search.trim().toLowerCase();
    return filterTree(tree, (node) => {
      const person = assigneeOf(node);
      const personKey = person ? person.employeeEmail || person.employeeName : "__unassigned";
      if (term && !`${node.name} ${node.summary}`.toLowerCase().includes(term)) return false;
      if (people.length && !people.includes(personKey)) return false;
      if (types.length && !types.some((t) => t.toLowerCase() === String(node.issueType || "Story").toLowerCase())) return false;
      if (quick.includes("mine") && String(person?.employeeId || "") !== String(myId)) return false;
      if (quick.includes("unassigned") && person) return false;
      if (quick.includes("hideDone") && isDoneStatus(node.status)) return false;
      if (quick.includes("activeSprint") && !node.sprint?.active) return false;
      return true;
    });
  }, [tree, filtering, search, people, types, quick, myId]);

  // while filtering everything opens, so a match never hides in a closed parent
  const rows = useMemo(() => visibleRows(filteredTree, expanded, filtering), [filteredTree, expanded, filtering]);

  const allNodes = useMemo(() => flattenAll(tree), [tree]);
  const counts = useMemo(() => countByLevel(tree), [tree]);
  const doneCount = allNodes.filter((n) => isDoneStatus(n.status)).length;

  /* people who hold work here, most-loaded first, for the avatar filter */
  const assignees = useMemo(() => {
    const map = new Map();
    allNodes.forEach((node) => {
      const person = assigneeOf(node);
      const key = person ? person.employeeEmail || person.employeeName : "__unassigned";
      const entry = map.get(key) || { key, name: person ? person.employeeName || person.employeeEmail : "Unassigned", count: 0 };
      entry.count += 1;
      map.set(key, entry);
    });
    contributors.forEach((m) => {
      const key = m.employeeEmail || m.employeeName;
      if (key && !map.has(key)) map.set(key, { key, name: m.employeeName || m.employeeEmail, count: 0 });
    });
    return [...map.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [allNodes, contributors]);

  const toggleIn = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  /* --------------------------------------------------------------- actions */
  const markBusy = (id, on) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleStatusChange = async (issue, status) => {
    const id = String(issue._id);
    const previous = tree;
    setTree((nodes) => patchNode(nodes, id, { status }));
    markBusy(id, true);
    try {
      await dispatch(updateIssue({ issueId: id, payload: { status } })).unwrap();
      await load(); // parents' bars move with it
    } catch (error) {
      setTree(previous);
      toast.error(errorText(error, `Could not move ${issue.name} to ${status}`));
    } finally {
      markBusy(id, false);
    }
  };

  const handleCreate = async (parent, { summary, issueType }) => {
    setCreating(true);
    try {
      await axios.post(
        `${API_BASE_URL}/issues/createBacklogIssue`,
        { project: projectId, summary, issueType, status: statuses[0] || "To Do", ...(parent ? { parent: parent._id } : {}) },
        { headers: authHeaders() }
      );
      if (parent) saveExpanded(new Set(expanded).add(String(parent._id)));
      await load();
      toast.success(`${issueType} added${parent ? ` under ${parent.name}` : ""}`);
      setAddingUnder(null);
    } catch (error) {
      toast.error(errorText(error, `Could not add the ${issueType.toLowerCase()}`));
    } finally {
      setCreating(false);
    }
  };

  const handleSetParent = async (issue, parentId) => {
    try {
      const { data } = await axios.put(
        `${API_BASE_URL}/issues/setParent/${issue._id}`,
        { parentId },
        { headers: authHeaders() }
      );
      if (parentId) saveExpanded(new Set(expanded).add(String(parentId)));
      await load();
      toast.success(parentId ? `${issue.name} moved under ${data?.parentName}` : `${issue.name} is now top level`);
    } catch (error) {
      toast.error(errorText(error, "Could not move that issue"));
      throw error;
    }
  };

  const handleAddChild = (node) => {
    saveExpanded(new Set(expanded).add(String(node._id)));
    setAddingUnder(String(node._id));
  };

  /* ---------------------------------------------------------------- render */
  const visiblePeople = assignees.slice(0, MAX_AVATARS);
  const morePeople = assignees.length - visiblePeople.length;
  const percent = allNodes.length ? Math.round((doneCount / allNodes.length) * 100) : 0;

  return (
    <div className="work-items-page">
      <header className="wh-hero">
        <div className="wh-hero__crumbs"><BreadcrumbsComponent /></div>
        <div className="wh-hero__main">
          <span className="wh-hero__tile" aria-hidden="true"><AccountTreeRoundedIcon /></span>
          <div>
            <h1 className="wh-hero__title">Work items</h1>
            <div className="wh-hero__meta">
              <span className="wh-chip"><strong>{counts.epics}</strong> {counts.epics === 1 ? "epic" : "epics"}</span>
              <span className="wh-chip"><strong>{counts.stories}</strong> {counts.stories === 1 ? "story" : "stories"}</span>
              <span className="wh-chip"><strong>{counts.tasks}</strong> {counts.tasks === 1 ? "task" : "tasks & bugs"}</span>
              {allNodes.length ? (
                <Tooltip title={`${doneCount} of ${allNodes.length} work items done`}>
                  <span className="wh-chip wh-chip--progress">
                    <span className="wh-progress"><span style={{ width: `${percent}%` }} /></span>
                    {percent}% done
                  </span>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <div className="wh-toolbar">
        <label className="wh-search">
          <SearchRoundedIcon />
          <input
            type="search"
            placeholder="Search work"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search work items"
          />
        </label>

        <div className="wh-people" role="group" aria-label="Filter by assignee">
          {visiblePeople.map((p) => (
            <Tooltip key={p.key} title={`${p.name} · ${p.count} ${p.count === 1 ? "item" : "items"}`}>
              <button
                type="button"
                className={`wh-person ${people.includes(p.key) ? "is-selected" : ""}`}
                aria-pressed={people.includes(p.key)}
                aria-label={`Show work for ${p.name}`}
                onClick={() => toggleIn(people, setPeople, p.key)}
              >
                {p.key === "__unassigned" ? (
                  <span className="wh-avatar-empty"><PersonOutlineRoundedIcon /></span>
                ) : (
                  <BackgroundLetterAvatarsLogo LogoTitle={p.name} RequiredFontSize="11px !important" />
                )}
              </button>
            </Tooltip>
          ))}
          {morePeople > 0 ? (
            <button type="button" className="wh-person wh-person--more" onClick={(e) => setPeopleAnchor(e.currentTarget)} aria-label={`${morePeople} more people`}>
              +{morePeople}
            </button>
          ) : null}
          <Menu anchorEl={peopleAnchor} open={Boolean(peopleAnchor)} onClose={() => setPeopleAnchor(null)}>
            {assignees.slice(MAX_AVATARS).map((p) => (
              <MenuItem key={p.key} dense onClick={() => toggleIn(people, setPeople, p.key)}>
                <Checkbox size="small" checked={people.includes(p.key)} />
                <ListItemText primary={p.name} secondary={`${p.count} items`} />
              </MenuItem>
            ))}
          </Menu>
        </div>

        <button type="button" className={`wh-tool ${types.length ? "is-active" : ""}`} onClick={(e) => setFilterAnchor(e.currentTarget)}>
          <FilterListRoundedIcon />
          Filter{types.length ? ` · ${types.length}` : ""}
        </button>
        <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)}>
          <ListSubheader>Work type</ListSubheader>
          {TYPE_FILTERS.map((t) => (
            <MenuItem key={t} dense onClick={() => toggleIn(types, setTypes, t)}>
              <Checkbox size="small" checked={types.includes(t)} />
              <ListItemText primary={t} />
            </MenuItem>
          ))}
        </Menu>

        <button type="button" className={`wh-tool ${quick.length ? "is-active" : ""}`} onClick={(e) => setQuickAnchor(e.currentTarget)}>
          Quick filters{quick.length ? ` · ${quick.length}` : ""}
          <KeyboardArrowDownRoundedIcon />
        </button>
        <Menu anchorEl={quickAnchor} open={Boolean(quickAnchor)} onClose={() => setQuickAnchor(null)}>
          {QUICK_FILTERS.map((f) => (
            <MenuItem key={f.key} dense onClick={() => toggleIn(quick, setQuick, f.key)}>
              <Checkbox size="small" checked={quick.includes(f.key)} />
              <ListItemText primary={f.label} />
            </MenuItem>
          ))}
        </Menu>

        {filtering ? (
          <button type="button" className="wh-link" onClick={() => { setSearch(""); setPeople([]); setTypes([]); setQuick([]); }}>
            Clear filters
          </button>
        ) : null}

        <div className="wh-toolbar__end">
          <Tooltip title="Expand all">
            <button type="button" className="wh-icon-tool" aria-label="Expand all" onClick={() => saveExpanded(new Set(idsWithChildren(tree)))}>
              <UnfoldMoreRoundedIcon />
            </button>
          </Tooltip>
          <Tooltip title="Collapse all">
            <button type="button" className="wh-icon-tool" aria-label="Collapse all" onClick={() => saveExpanded(new Set())}>
              <UnfoldLessRoundedIcon />
            </button>
          </Tooltip>
          <button type="button" className="wh-create" onClick={() => setAddingUnder("root")}>
            <AddRoundedIcon />
            Create
          </button>
        </div>
      </div>

      {loading ? (
        <div className="wh-table wh-table--loading" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="wh-skeleton-row" />)}
        </div>
      ) : loadError ? (
        <div className="wh-empty" role="alert">
          <p>{loadError}</p>
          <button type="button" className="wh-btn is-primary" onClick={load}>Try again</button>
        </div>
      ) : (
        <>
          <WorkHierarchyTable
            rows={rows}
            statuses={statuses}
            busyIds={busyIds}
            addingUnder={addingUnder}
            creating={creating}
            onToggle={toggle}
            onOpen={(node) => setOpenIssue(node)}
            onStatusChange={handleStatusChange}
            onAddChild={handleAddChild}
            onCreateChild={handleCreate}
            onCancelAdd={() => setAddingUnder(null)}
            onChangeParent={(node) => setParentTarget(node)}
            onRemoveParent={(node) => handleSetParent(node, null).catch(() => {})}
          />
          {!rows.length && addingUnder !== "root" ? (
            <div className="wh-empty">
              {filtering ? (
                <p>No work items match these filters.</p>
              ) : (
                <>
                  <p><strong>No work items yet.</strong> Start with an epic, then break it into stories and tasks.</p>
                  <button type="button" className="wh-btn is-primary" onClick={() => setAddingUnder("root")}>Create an epic</button>
                </>
              )}
            </div>
          ) : null}
          {!filtering && tree.length && !tree.some((n) => n.children?.length) && tree.some((n) => levelOf(n.issueType) === 0) ? (
            <p className="wh-hint">
              Tip: use <strong>⋯ → Move under…</strong> on a story or task to put it under its epic.
            </p>
          ) : null}
        </>
      )}

      {parentTarget ? (
        <ParentPickerDialog
          open
          issue={parentTarget}
          roots={tree}
          onClose={() => setParentTarget(null)}
          onSave={handleSetParent}
        />
      ) : null}

      {openIssue ? (
        <TaskWorkFlowModalPage
          open
          selectedissueId={openIssue._id}
          issueName={openIssue.name}
          issueStatusList={(project?.IssueStatus || []).filter((s) => s?.title)}
          handleChangeModalOpen={() => {
            setOpenIssue(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
};

export default WorkItemsPage;
